/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useRef, useCallback, useEffect } from "react";

import ChartJS from "chart.js/auto";
import type {ChartConfiguration, ChartData, ChartOptions, ChartType} from "chart.js";

// antd:
import { Form } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getAppState, getTheme, getLng, getDict, getUser, getSafePtr, changeAppState } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

// Внутренние настройки компонента. Они собираются из XML type/attributes
// и потом превращаются в Chart.js configuration.
type Tchart = {
  type:ChartType,
  responsive:boolean,
  maintainAspectRatio:boolean,
  legend:boolean,
  animation?:boolean,
  parsing?:boolean,
  indexAxis?:"x" | "y"
};

const chartTypes:Array<ChartType> = ["bar", "line", "pie", "doughnut", "radar", "polarArea", "scatter", "bubble"];

// В XML часто передают JSON с одинарными кавычками, как в других UI-компонентах.
// Здесь приводим такую строку к объекту, не заставляя пользователя писать строгий JSON.
const parseJSONAttr = (attr:any) => {
  if (!attr || !rz.isString(attr)) { return undefined }
  const prepared = rz.strReplaceAll(attr, "'", "\"").trim();
  if (!prepared.startsWith("{") || !prepared.endsWith("}")) { return undefined }
  try { return rz.objFromJSON(prepared) }
  catch (err) { console.log(err); return undefined }
};

// XML-атрибуты приходят строками, поэтому boolean нужно приводить явно.
const parseBoolean = (value:any, defaultValue:boolean) => {
  if (value === true || value === "true") { return true }
  if (value === false || value === "false") { return false }
  return defaultValue;
};

// CSS width/height можно задать и числом, и строкой вроде "100%" или "calc(...)";
// Chart.js живет внутри div-обертки, поэтому размер задаем именно обертке.
const parseSize = (value:any):string | number | undefined => {
  if (rz.isUndefinedOrNull(value) || value === "") { return undefined }
  if (typeof value === "number" && Number.isFinite(value)) { return value }
  if (rz.canbeNumber(value)) { return Number(value) }
  return value;
};

// Универсальная нормализация данных в формат Chart.js.
// Поддерживаем полный config, чистый ChartData, массив чисел и массив объектов,
// чтобы компонент был удобен и для простых XML-примеров, и для реальных данных из app script.
const getChartDataFromAny = (source:any, attributes:{[key:string]:any}, fallbackLabel:string):ChartData => {
  if (!source) { return {labels: [], datasets: []} }

  if (source.data && source.data.labels && source.data.datasets) { return source.data }
  if (source.chartData && source.chartData.labels && source.chartData.datasets) { return source.chartData }
  if (source.labels && source.datasets) { return source }

  if (rz.isArray(source)) {
    const rows = source;
    if (!rows.length) { return {labels: [], datasets: []} }
    if (typeof rows[0] === "number") {
      return {
        labels: rows.map((_, i) => String(i + 1)),
        datasets: [{label: fallbackLabel, data: rows}]
      };
    }

    const labelField = attributes.labelField || attributes.labelsField || "label";
    const valueField = attributes.valueField || attributes.dataField || "value";
    return {
      labels: rows.map((rr, i) => (rr[labelField] || rr.name || rr.title || String(i + 1))),
      datasets: [{
        label: attributes.datasetLabel || fallbackLabel,
        data: rows.map((rr) => (rz.isUndefinedOrNull(rr[valueField]) ? rr.value : rr[valueField]))
      }]
    };
  }

  return {labels: [], datasets: []};
};

// Если пользователь не передал цвета dataset-ов, задаем базовую палитру под текущую тему.
// Это делает первый рендер читаемым без обязательной настройки backgroundColor/borderColor.
const applyDefaultDatasetColors = (data:ChartData, theme:string):ChartData => {
  const colors = (theme === "dark"
    ? ["#7dd3fc", "#fca5a5", "#86efac", "#fde68a", "#c4b5fd", "#f0abfc", "#5eead4", "#fdba74"]
    : ["#2563eb", "#dc2626", "#16a34a", "#ca8a04", "#7c3aed", "#c026d3", "#0f766e", "#ea580c"]);

  return {
    ...data,
    datasets: (data.datasets || []).map((dataset:any, i:number) => ({
      ...dataset,
      borderColor: dataset.borderColor || colors[i % colors.length],
      backgroundColor: dataset.backgroundColor || colors[i % colors.length] + "55"
    }))
  };
};

const RZChart:FC<TuniversalElementProps> = ({attributes}) => {
  RZChart.displayName = "Chart";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const lng = useAppSelector(getLng);
  const dct = useAppSelector(getDict);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  const {color, backgroundColor, borders} = elState.attributes;
  // Namespace приложения хранит пользовательские функции из app .js.
  // Через него работают data/options/onClick по аналогии с AGgrid и Jodit.
  const rootNamespace = useMemo<{[key:string]:any}>(() => ((window as any)[rootAttributes.key || rz.strMakeID(24)] || {}), [rootAttributes.key]);

  // Chart.js создает императивный объект поверх canvas, поэтому держим оба ref:
  // canvasRef нужен для создания, chartRef - для destroy() перед пересозданием и unmount.
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<ChartJS | null>(null);

  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState?.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const chartType = useMemo<Tchart>(() => {
    const typ:Tchart = {type: "bar", responsive: true, maintainAspectRatio: false, legend: true};

    // Короткие флаги в type позволяют писать XML в стиле остальных компонентов:
    // type="line noLegend" вместо набора отдельных атрибутов.
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~chartTypes.indexOf(tt as ChartType)) { typ.type = tt as ChartType }
      else if (~["horizontalBar", "horizontalbar"].indexOf(tt)) { typ.type = "bar"; typ.indexAxis = "y" }
      else if (~["noLegend", "nolegend", "hideLegend", "hidelegend"].indexOf(tt)) { typ.legend = false }
      else if (~["noAnimation", "noanimation"].indexOf(tt)) { typ.animation = false }
      else if (~["noParsing", "noparsing"].indexOf(tt)) { typ.parsing = false }
    });

    if (attributes.chartType && ~chartTypes.indexOf(attributes.chartType)) { typ.type = attributes.chartType }
    else if (attributes.type && ~chartTypes.indexOf(attributes.type)) { typ.type = attributes.type }
    if (attributes.indexAxis) { typ.indexAxis = attributes.indexAxis }
    if (attributes.responsive) { typ.responsive = parseBoolean(attributes.responsive, typ.responsive) }
    if (attributes.maintainAspectRatio) { typ.maintainAspectRatio = parseBoolean(attributes.maintainAspectRatio, typ.maintainAspectRatio) }
    if (attributes.legend) { typ.legend = parseBoolean(attributes.legend, typ.legend) }
    if (attributes.animation) { typ.animation = parseBoolean(attributes.animation, true) }
    if (attributes.parsing) { typ.parsing = parseBoolean(attributes.parsing, true) }

    return typ;
  }, [type, attributes]);

  const title = useMemo<string | undefined>(() => (
    elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng]
    || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]
  ), [elState.attributes, lng, rlng, attributes]);

  const title2 = useMemo<string | undefined>(() => (
    elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng]
    || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]
  ), [elState?.attributes, attributes, lng, rlng]);

  const tooltip = useMemo<string | undefined>(() => (
    elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]
  ), [elState.attributes, lng, rlng]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (borders && !rz.arrContains(userClss, /^rz-borders-/igm)) {
      borders.split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
        .forEach((ff:string) => { if (!~userClss.indexOf("rz-borders-" + ff)) { userClss.push("rz-borders-" + ff) } });
    }
    return userClss;
  }, [elState?.userClass, elState?.disabled, borders, permissions.disabled]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    if (!est.boxSizing) { est.boxSizing = "border-box" }
    if (!est.position) { est.position = "relative" }
    if (!est.width) { est.width = (parseSize(attributes.width) || "100%") as any }
    if (!est.height) { est.height = (parseSize(attributes.height) || 320) as any }
    return est;
  }, [elState.userStyle, elState?.visible, color, backgroundColor, theme, permissions.visible, attributes.width, attributes.height]);

  const applyClientState = useCallback((ret:any) => {
    if (rz.isUndefinedOrNull(ret)) { return }
    if (ret?.err) { console.log(ret.err) }
    if (ret?.state?.fullPath && ret?.state?.newState) {
      dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
    }
  }, [dispatch, rootAttributes.key]);

  // Первичная загрузка данных.
  // Источник может быть JSON-атрибутом или именем функции в window[appkey].
  // Результат кладем в Redux state, чтобы график дальше жил как обычный UI-элемент.
  useEffect(() => {
    const attr = attributes?.chartData || attributes?.chartdata || attributes?.data || attributes?.rowData || attributes?.rowdata || attributes?.rows || attributes?.onLoadData || attributes?.onloaddata || "";
    if (!attr || !elState.loading) { return }

    if (rz.isString(attr) && attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = parseJSONAttr(attr);
      if (jsn) {
        dispatch(changeAppState({
          appkey: rootAttributes.key,
          fullPath,
          // @ts-ignore
          newState: {data: getChartDataFromAny(jsn, attributes, title || String(key)), value: jsn, loading: false}
        }));
        if (fnc.config?.isDev) { console.log("<Chart key='" + key + "'> -> onLoadData('" + rootAttributes.key + "'): Loaded data from JSON attribute") }
      }
    }
    else if (attr && rootNamespace && rootNamespace[attr]) {
      const clientFNC = rootNamespace[attr];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}).then((ret) => {
          if (!ret) { return }
          dispatch(changeAppState({
            appkey: rootAttributes.key,
            fullPath,
            // @ts-ignore
            newState: {data: getChartDataFromAny(ret, attributes, title || String(key)), value: ret, loading: false}
          }));
          if (fnc.config?.isDev) { console.log("<Chart key='" + key + "'> -> onLoadData('" + rootAttributes.key + "'): Loaded data from function") }
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe});
        if (ret && ret.then) {
          ret.then((pret) => {
            if (!pret) { return }
            dispatch(changeAppState({
              appkey: rootAttributes.key,
              fullPath,
              // @ts-ignore
              newState: {data: getChartDataFromAny(pret, attributes, title || String(key)), value: pret, loading: false}
            }));
            if (fnc.config?.isDev) { console.log("<Chart key='" + key + "'> -> onLoadData('" + rootAttributes.key + "'): Loaded data from function") }
          }).catch((err) => { console.log(err) });
        }
        else if (ret) {
          dispatch(changeAppState({
            appkey: rootAttributes.key,
            fullPath,
            // @ts-ignore
            newState: {data: getChartDataFromAny(ret, attributes, title || String(key)), value: ret, loading: false}
          }));
          if (fnc.config?.isDev) { console.log("<Chart key='" + key + "'> -> onLoadData('" + rootAttributes.key + "'): Loaded data from function") }
        }
      }
    }
  }, [attributes, dct, dispatch, elState.loading, fullPath, key, lng, rootAttributes.key, rootNamespace, safe, title, user]);

  // Собираем options из полного config, XML-атрибутов и/или функции в app namespace.
  // Здесь же подключаем onClick, потому что Chart.js ожидает callback внутри options.
  const chartOptions = useMemo<ChartOptions>(() => {
    const fullConfig = (elState.value && rz.isObject(elState.value) ? elState.value : {});
    let options:any = (fullConfig.options && rz.isObject(fullConfig.options) ? rz.objClone(fullConfig.options) : {});
    const attr = attributes.options || attributes.chartOptions || attributes.chartoptions || "";

    if (rz.isString(attr) && attr.startsWith("{") && attr.endsWith("}")) {
      const jsn = parseJSONAttr(attr); if (jsn) { options = {...options, ...jsn} }
    }
    else if (attr && rootNamespace && rootNamespace[attr] && rz.isFunction(rootNamespace[attr])) {
      const ret = rootNamespace[attr]({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe});
      if (ret && rz.isObject(ret)) { options = {...options, ...ret} }
    }

    options.responsive = chartType.responsive;
    options.maintainAspectRatio = chartType.maintainAspectRatio;
    if (!rz.isUndefinedOrNull(chartType.animation)) { options.animation = chartType.animation }
    if (!rz.isUndefinedOrNull(chartType.parsing)) { options.parsing = chartType.parsing }
    if (chartType.indexAxis) { options.indexAxis = chartType.indexAxis }
    options.plugins = {
      ...(options.plugins || {}),
      legend: {...(options.plugins?.legend || {}), display: chartType.legend},
      title: {
        ...(options.plugins?.title || {}),
        display: Boolean(title || options.plugins?.title?.text),
        text: title || options.plugins?.title?.text
      }
    };

    const attrClick = attributes.onClick || attributes.onclick;
    if (attrClick && rootNamespace && rootNamespace[attrClick]) {
      options.onClick = (event:any, elements:Array<any>, chart:ChartJS) => {
        const clientFNC = rootNamespace[attrClick];
        if (fnc.config?.isDev) { console.log({action: "<Chart key='" + key + "'> -> onClick()"}); }
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe, event, elements, chart}).then(applyClientState);
        }
        else if (rz.isFunction(clientFNC)) {
          applyClientState(clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe, event, elements, chart}));
        }
      };
    }

    return options;
  }, [elState.value, attributes, rootNamespace, rootAttributes.key, key, user, lng, dct, safe, chartType, title, applyClientState]);

  // Финальный Chart.js config.
  // Если в value лежит полный config, а state.data еще пустой, используем fullConfig как источник,
  // иначе приоритет у актуального elState.data, который может обновляться через changeState.
  const chartConfig = useMemo<ChartConfiguration>(() => {
    const fullConfig = (elState.value && rz.isObject(elState.value) ? elState.value : {});
    const ctype = (fullConfig.type && ~chartTypes.indexOf(fullConfig.type) ? fullConfig.type : chartType.type);
    const stateData = elState.data;
    const dataSource = (Array.isArray(stateData) && !stateData.length && rz.objKeys(fullConfig).length ? fullConfig : (stateData || fullConfig));
    const data = applyDefaultDatasetColors(getChartDataFromAny(dataSource, attributes, title || String(key)), theme || "light");
    return {type: ctype, data, options: chartOptions};
  }, [attributes, chartOptions, chartType.type, elState.data, elState.value, key, theme, title]);

  // Жизненный цикл Chart.js: перед каждым пересозданием и при unmount обязательно destroy().
  // Без этого Chart.js оставляет старые canvas bindings и начинает конфликтовать с новым render.
  useEffect(() => {
    if (!canvasRef.current) { return undefined }
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null }
    chartRef.current = new ChartJS(canvasRef.current, chartConfig);

    return () => {
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null }
    };
  }, [chartConfig]);

  // Сохраняем поведение форм как у Jodit/Input: если родитель - <form>, компонент
  // автоматически оборачивается в Form.Item и получает label из title2.
  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  return wrapperForm(
    // eslint-disable-next-line react/no-unknown-property
    <div key={key} id={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}>
      <canvas ref={canvasRef} />
    </div>
  );
}

export default RZChart;
/* eslint-enable @typescript-eslint/no-unused-vars */
